// src/hooks/sync/useSharedPrayerSync.js
// Publishes the prayer requests the user chose to share, and removes the shared copy
// when a request is made private, deleted, or nobody is left to share it with.
// Mount once in Layout.
//
//   request.shareMode  'private' | 'mentor' | 'timothys' | 'pair' | 'tree' | 'circle' | 'people' | 'public'
//   request.shareUids  uids picked for 'people'
//
//   public → publicPrayers/{ownerUid}_{requestId}  (any signed-in user can read)
//   other  → circlePrayers/{ownerUid}_{requestId}  (only uids listed in `audience` can read)
//
// The copy is a snapshot: text, person, the checked points (with notes), and the answered
// state/testimony. It is refreshed automatically when the request changes.
import { useEffect, useRef, useState } from 'react';
import { collection, deleteDoc, doc, getDocs, limit, query, serverTimestamp, setDoc, updateDoc, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useSyncStore } from '@/hooks/sync/useSyncStore';
import { usePrayerPartners } from '@/hooks/sync/usePrayerPartners';
import { useDiscipleshipTracking } from '@/hooks/feature/useDiscipleshipTracking';
import { STORE_PRAYER_REQUESTS, STORE_USER_PROFILE, QUERY_LIMIT } from '@/lib/storeKeys';
import { collectionFor, resolveAudience, isLabelList } from '@/lib/prayerAudience';
import { useJournalStreakSync } from '@/hooks/sync/useJournalStreakSync';
import { useInviteLink } from '@/hooks/sync/useInviteLink';

const INCLUDE_NOTES = true;   // false = share only the point names, never the personal notes
const DEBOUNCE_MS   = 2000;

const sig = (p) => JSON.stringify([
  p.mode, p.ownerName, p.category, p.person, p.text, p.checklist, p.audience,
  !!p.answered, p.praise, p.dateAnswered ?? null, p.dateAdded ?? null,
]);

export function useSharedPrayerSync() {
  const { user } = useAuth();
  const uid = user?.uid && user.uid !== 'offline' ? user.uid : null;

  const [requests, , requestsReady] = useSyncStore(STORE_PRAYER_REQUESTS, []);
  const [profile] = useSyncStore(STORE_USER_PROFILE, {});
  const { disciples, ready: disciplesReady } = useDiscipleshipTracking();
  const { partners, linked, loaded, reload } = usePrayerPartners();
  useJournalStreakSync({ linked, loaded });
  useInviteLink({ partners, loaded, reload });

  const publishedRef = useRef(null);   // Map<docId, { col, sig }>
  const busyRef      = useRef(false);
  const firstPassRef = useRef(true);   // first pass of a session never deletes a copy only because its audience looks empty
  const [tick, setTick] = useState(0);

  // Links may change while the app stays open — refresh them when it returns to the foreground.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') reload(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [reload]);

  useEffect(() => {
    if (!uid || !db || !requestsReady || !loaded || !disciplesReady) return undefined;

    const reconcile = async () => {
      // 1. What is already published (once per session).
      if (!publishedRef.current) {
        const own = (name) => query(collection(db, name), where('ownerUid', '==', uid), limit(QUERY_LIMIT));
        const [pub, cir] = await Promise.all([getDocs(own('publicPrayers')), getDocs(own('circlePrayers'))]);
        const map = new Map();
        pub.docs.forEach((d) => map.set(d.id, { col: 'publicPrayers', sig: sig(d.data()) }));
        cir.docs.forEach((d) => map.set(d.id, { col: 'circlePrayers', sig: sig(d.data()) }));
        publishedRef.current = map;
      }
      const published = publishedRef.current;

      // 2. What should be published now.
      const ctx = {
        linkedUids: new Set(linked.map((p) => p.uid)),
        mentorUid:  profile?.mentorUid || '',
        disciples:  Array.isArray(disciples) ? disciples : [],
      };
      const ownerName = String(profile?.displayName || user?.displayName || '').trim().slice(0, 60) || 'A friend';
      const wanted  = new Set();   // sharing requested (even if nobody is in the audience yet)
      const desired = new Map();   // docId -> { colName, payload }

      (Array.isArray(requests) ? requests : []).forEach((r) => {
        const mode = r?.shareMode;
        if (!r?.id || !mode || mode === 'private') return;
        const colName = collectionFor(mode);
        const id = `${uid}_${r.id}`;
        wanted.add(id);
        const audience = mode === 'public' ? [] : resolveAudience(mode, { ...ctx, shareUids: r.shareUids });
        if (mode !== 'public' && audience.length === 0) return;
        desired.set(id, {
          colName,
          payload: {
            ownerUid: uid,
            ownerName,
            requestId: String(r.id),
            mode,
            category: String(r.category || 'personal').slice(0, 30),
            person:   String(r.person || '').slice(0, 200),
            text:     isLabelList(r.text, r.checklist) ? '' : String(r.text || '').slice(0, 3000),
            checklist: (Array.isArray(r.checklist) ? r.checklist : [])
              .filter((i) => i?.checked)
              .slice(0, 30)
              .map((i) => (INCLUDE_NOTES
                ? { label: String(i.label || '').slice(0, 120), note: String(i.note || '').slice(0, 1000) }
                : { label: String(i.label || '').slice(0, 120) })),
            audience,
            answered:     !!r.answered,
            praise:       String(r.praise || '').slice(0, 3000),
            dateAnswered: r.dateAnswered || null,
            dateAdded:    r.dateAdded || null,
          },
        });
      });

      // 3. Create / update / move.
      for (const [id, { colName, payload }] of desired) {
        const ex = published.get(id);
        const nextSig = sig(payload);
        if (ex && ex.col === colName && ex.sig === nextSig) continue;
        try {
          if (ex && ex.col !== colName) await deleteDoc(doc(db, ex.col, id));
          if (ex && ex.col === colName) {
            await updateDoc(doc(db, colName, id), { ...payload, updatedAt: serverTimestamp() });
          } else {
            await setDoc(doc(db, colName, id), { ...payload, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
          }
          published.set(id, { col: colName, sig: nextSig });
        } catch (err) {
          console.warn('[useSharedPrayerSync] publish failed:', err?.code || err);
        }
      }

      // 4. Remove copies that should no longer exist.
      for (const [id, ex] of [...published]) {
        if (desired.has(id)) continue;
        if (wanted.has(id) && firstPassRef.current) continue; // audience may just not have loaded yet
        try {
          await deleteDoc(doc(db, ex.col, id));
          published.delete(id);
        } catch (err) {
          console.warn('[useSharedPrayerSync] unpublish failed:', err?.code || err);
        }
      }
      firstPassRef.current = false;
    };

    const timer = setTimeout(async () => {
      if (busyRef.current) { setTick((t) => t + 1); return; }
      busyRef.current = true;
      try { await reconcile(); }
      catch (err) { console.warn('[useSharedPrayerSync] failed:', err?.code || err); }
      finally { busyRef.current = false; }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [uid, user, requests, requestsReady, loaded, disciplesReady, linked, profile, disciples, tick]);
}

export default useSharedPrayerSync;